import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import DashboardLayout from "../../components/DashboardLayout";
import { ProductFormModal } from "../../components/ProductFormModal";
import { router } from "@inertiajs/react";
import { Product, User, Category, Brand } from "@/types";
import { Plus, Edit, Trash2, Search, Filter, Package } from "lucide-react";

interface PageProps {
  auth: {
    admin: User;
  };
  products: Product[];
  categories: Category[];
  brands: Brand[];
  store_logo?: string;
  store_name?: string;
}

const Products = ({
  products,
  categories: initialCategories,
  brands: initialBrands,
  store_logo,
  store_name,
}: PageProps) => {
  const sanitizedProducts = useMemo(() => {
    return (products || []).map((p) => ({
      ...p,
      price: typeof p.price === "string" ? parseFloat(p.price) : p.price,
    }));
  }, [products]);

  const [categories] = useState(initialCategories || []);
  const [brands] = useState(initialBrands || []);

  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [selectedType, setSelectedType] = useState("all");

  const filteredProducts = sanitizedProducts.filter((product) => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description &&
        product.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory =
      selectedCategory === "all" ||
      product.category_id?.toString() === selectedCategory;
    const matchesBrand =
      selectedBrand === "all" || product.brand_id?.toString() === selectedBrand;
    const matchesType =
      selectedType === "all" ||
      (selectedType === "exclusivo" && product.type === "FeaturedProduct") ||
      (selectedType === "normal" && product.type !== "FeaturedProduct");
    return matchesSearch && matchesCategory && matchesBrand && matchesType;
  });

  const handleDeleteProduct = (id: number) => {
    if (confirm("¿Estás seguro de que quieres eliminar este producto?")) {
      router.delete(`/admin/products/${id}`, {
        preserveScroll: true,
      });
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleAddNewProduct = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const getStockBadgeVariant = (stock: number) => {
    if (stock === 0) return "destructive";
    if (stock < 10) return "secondary";
    return "default";
  };

  return (
    <DashboardLayout
      title="Gestión de Productos"
      storeLogo={store_logo}
      storeName={store_name}
    >
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Gestión de Productos
            </h1>
            <p className="text-slate-50 text-lg">
              Administra el catálogo de productos de tu tienda
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button onClick={handleAddNewProduct} className="bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl transition-all duration-200">
              <Plus className="h-5 w-5 mr-2" />
              Nuevo Producto
            </Button>
          </div>
        </div>
      </div>
      {/* Filters */}
      <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center space-x-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-r from-slate-500 to-slate-600 shadow-lg">
              <Filter className="h-6 w-6 text-white" />
            </div>
            <span>Filtros de Búsqueda</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="space-y-3">
              <Label htmlFor="search" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Buscar Productos</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input
                  id="search"
                  placeholder="Buscar por nombre o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-11 h-11 border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg"
                />
              </div>
            </div>
            <div className="space-y-3">
              <Label htmlFor="category" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Categoría</Label>
              <Select
                onValueChange={setSelectedCategory}
                defaultValue={selectedCategory}
              >
                <SelectTrigger id="category" className="w-full h-11 border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg">
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map((category) => (
                    <SelectItem
                      key={category.id}
                      value={category.id.toString()}
                    >
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label htmlFor="brand" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Marca</Label>
              <Select
                onValueChange={setSelectedBrand}
                defaultValue={selectedBrand}
              >
                <SelectTrigger id="brand" className="w-full h-11 border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg">
                  <SelectValue placeholder="Todas las marcas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las marcas</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id.toString()}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label htmlFor="type" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tipo</Label>
              <Select
                onValueChange={setSelectedType}
                defaultValue={selectedType}
              >
                <SelectTrigger id="type" className="w-full h-11 border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg">
                  <SelectValue placeholder="Todos los tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="exclusivo">Exclusivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Total de Productos</Label>
              <div className="h-11 flex items-center">
                <div className="text-3xl font-bold bg-gradient-to-r from-slate-600 to-slate-700 bg-clip-text text-transparent">
                  {filteredProducts.length}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PDF Download */}
      {selectedCategory !== "all" && categories.find(c => c.id.toString() === selectedCategory)?.pdf_url && (
        <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
          <CardHeader className="pb-6">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 shadow-lg">
                <Package className="h-6 w-6 text-white" />
              </div>
              Descargar Catálogo PDF
            </CardTitle>
            <CardDescription className="text-base text-slate-600 dark:text-slate-400">
              Descarga el catálogo PDF para la categoría seleccionada
            </CardDescription>
          </CardHeader>
          <CardContent>
            <a
              href={categories.find(c => c.id.toString() === selectedCategory)?.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg hover:from-red-600 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
            >
              <Package className="h-5 w-5" />
              Descargar PDF
            </a>
          </CardContent>
        </Card>
      )}

      {/* Products Table */}
      <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-r from-slate-500 to-slate-600 shadow-lg">
              <Package className="h-6 w-6 text-white" />
            </div>
            Todos los Productos
          </CardTitle>
          <CardDescription className="text-base text-slate-600 dark:text-slate-400">
            Una lista completa de todos los productos en tu tienda.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm text-left">
              <thead className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700">
                <tr>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Imagen</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Nombre</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">SKU</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Categoría</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Marca</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Tipo</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Promoción</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Precio</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Precio Mercado</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Stock</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300">Carrito</th>
                  <th className="p-4 font-semibold text-slate-700 dark:text-slate-300 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr key={product.id} className={`border-b border-slate-200 dark:border-slate-700 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 dark:hover:from-blue-900/10 dark:hover:to-indigo-900/10 transition-all duration-200 ${index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50/50 dark:bg-slate-800/50'}`}>
                    <td className="p-4">
                      <img
                        src={
                          (product.images && product.images[0]) || "https://picsum.photos/150"
                        }
                        alt={product.name}
                        className="w-14 h-14 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow"
                      />
                    </td>
                    <td className="p-4 font-semibold text-slate-800 dark:text-slate-200">{product.name}</td>
                    <td className="p-4 text-slate-600 dark:text-slate-400 font-medium">{product.sku}</td>
                    <td className="p-4 text-slate-600 dark:text-slate-400">
                      {product.category_name}
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-400">
                      {product.brand_name}
                    </td>
                      <td className="p-4">
                        <Badge variant={product.type === "FeaturedProduct" ? "default" : "secondary"} className={product.type === "FeaturedProduct" ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white" : ""}>
                          {product.type === "FeaturedProduct"
                            ? "Exclusivo"
                            : "Normal"}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={product.has_active_promotion ? "default" : "secondary"} className={product.has_active_promotion ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : ""}>
                          {product.has_active_promotion ? "Sí" : "No"}
                        </Badge>
                      </td>
                      <td className="p-4 font-semibold text-green-600 dark:text-green-400">
                        S/. {(Number(product.price) || 0).toFixed(2)}
                      </td>
                     <td className="p-4 text-slate-500 dark:text-slate-400">
                       S/. {(Number(product.market_price) || 0).toFixed(2)}
                     </td>
                     <td className="p-4">
                       <Badge variant={getStockBadgeVariant(product.stock)} className={
                         product.stock === 0
                           ? "bg-gradient-to-r from-red-500 to-rose-500 text-white"
                           : product.stock < 10
                             ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-white"
                             : "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                       }>
                         {product.stock > 0
                           ? `${product.stock} en stock`
                           : "Sin Stock"}
                       </Badge>
                     </td>
                     <td className="p-4">
                        <Badge variant={product.cart_enabled ? "default" : "secondary"} className={product.cart_enabled ? "bg-gradient-to-r from-slate-500 to-slate-600 text-white" : ""}>
                         {product.cart_enabled ? "Habilitado" : "Deshabilitado"}
                       </Badge>
                     </td>
                     <td className="p-4 text-right">
                       <div className="flex items-center justify-end space-x-3">
                         <Button
                           variant="outline"
                           size="sm"
                           onClick={() => handleEditProduct(product)}
                           className="hover:bg-slate-50 dark:hover:bg-slate-900/20 border-slate-400 dark:border-slate-600 text-slate-700 dark:text-slate-400 hover:border-slate-500 dark:hover:border-slate-500 transition-all duration-200"
                         >
                           <Edit className="h-4 w-4" />
                         </Button>
                         <Button
                           variant="outline"
                           size="sm"
                           onClick={() => handleDeleteProduct(product.id)}
                           className="hover:bg-red-50 dark:hover:bg-red-900/20 border-red-400 dark:border-red-600 text-red-700 dark:text-red-400 hover:border-red-500 dark:hover:border-red-500 transition-all duration-200"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
         </CardContent>
       </Card>

      <ProductFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        product={editingProduct}
        categories={categories}
        brands={brands}
      />
    </DashboardLayout>
  );
};

export default Products;
